import { useMemo } from 'react';
import { arcFlags } from '../../../lib/s4/arcFlags';
import { logoColor } from '../../../lib/s4/cover';
import { arcDefaults, castDefaults, endingDefaults } from '../../../lib/s4/defaults';
import {
  STORY_ARC_KINDS,
  type ArcDef,
  type CastDef,
  type EndingDef,
  type StoryTeaserDef,
} from '../../../lib/s4/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import { MISSION_VISIBILITIES, TRIGGER_STATUSES } from '../../../lib/validate';
import { ConditionPicker } from '../../story/ConditionPicker';
import { relErrors } from '../../story/relErrors';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  SelectField,
  SelectInput,
  StringListEditor,
  TextField,
} from '../fields';
import { useContentBundle, type Option } from '../refs';
import type { FormProps } from '../types';
import { LoadoutEditor } from './LoadoutEditor';
import { CastLookField } from '../CastLookField';

const opts = (list: readonly string[]): Option[] => list.map((v) => ({ value: v, label: v }));

/**
 * An arc (`arcs[]`, shared-cs `ArcDef`, ADR 0042): the shelf row. Basics and the masthead logo, the production unlock, issues in reading
 * order (missions with a Story block), the fixed cast with default loadouts, endings (first match wins, the last without a condition),
 * coming-soon teasers, and the per-arc flags list. Absent fields show the C# defaults.
 */
export function ArcForm({ value, onChange, errors }: FormProps<ArcDef>) {
  const bundle = useContentBundle();
  const a = withDefaults(arcDefaults(), value) as Required<ArcDef>;
  const set = (patch: Partial<ArcDef>) => onChange({ ...value, ...patch });
  const logo = { text: '', color: '', sprite: '', ...a.logo };
  const unlock = { afterIssue: '', triggerId: '', ...a.unlock };

  const missions = (bundle.missions ?? []).flatMap((m) => (m?.id ? [m] : []));
  const storyMissions = missions.filter((m) => m.story);
  const issueOpts = storyMissions.map((m) => ({
    value: m.id ?? '',
    label: `${m.story?.arcId === a.id ? '' : `[${m.story?.arcId || 'no arc'}] `}#${m.story?.number ?? '?'} ${m.title || m.id} (${m.id})`,
  }));
  const allIssueOpts = storyMissions.map((m) => ({
    value: m.id ?? '',
    label: `${m.story?.arcId}: #${m.story?.number ?? '?'} ${m.title || m.id}`,
  }));
  const triggerOpts = (bundle.triggers ?? []).flatMap((t) =>
    t?.id && t.action === 'start_mission'
      ? [{ value: t.id, label: `${t.title || t.id} (${t.id})` }]
      : [],
  );
  const pronounOpts = (bundle.pronounSets ?? []).flatMap((p) =>
    p?.id ? [{ value: p.id, label: p.id }] : [],
  );
  const npcOpts = (bundle.npcs ?? []).flatMap((n) =>
    n?.id ? [{ value: n.id, label: `${n.name || n.id} (${n.id})` }] : [],
  );
  const chainOpts = (bundle.beatChains ?? []).flatMap((c) =>
    c?.id ? [{ value: c.id, label: `${c.title || c.id} (${c.id})` }] : [],
  );
  const cosmeticOpts = (bundle.cosmetics ?? []).flatMap((c) =>
    c?.id ? [{ value: c.id, label: `${c.name || c.id} (${c.id})` }] : [],
  );
  const flags = useMemo(() => arcFlags(bundle, value ?? {}), [bundle, value]);

  return (
    <>
      <Fieldset legend="Arc">
        <TextField
          label="Id"
          mono
          value={a.id}
          issues={errors.id}
          onChange={(v) => set({ id: v })}
        />
        <TextField
          label="Title"
          value={a.title}
          issues={errors.title}
          onChange={(v) => set({ title: v })}
        />
        <SelectField
          label="Kind"
          value={a.kind}
          options={opts(STORY_ARC_KINDS)}
          hint="main: the main story (may continue) · oneoff: a complete arc."
          issues={errors.kind}
          onChange={(v) => set({ kind: v ?? 'oneoff' })}
        />
        <NumberField
          label="Order"
          integer
          min={undefined}
          value={a.order}
          hint="Shelf order; unique."
          issues={errors.order}
          onChange={(v) => set({ order: v ?? 0 })}
        />
        <TextField
          label="Blurb"
          multiline
          value={a.blurb}
          hint='The line under the title ("One-off. A warehouse club, a biker gang, one long weekend.").'
          issues={errors.blurb}
          onChange={(v) => set({ blurb: v })}
        />
        <SelectField
          label="Status"
          value={a.status}
          options={opts(TRIGGER_STATUSES)}
          hint="coming_soon shows black covers only."
          issues={errors.status}
          onChange={(v) => set({ status: v ?? '' })}
        />
        <SelectField
          label="Visibility"
          value={a.visibility}
          options={opts(MISSION_VISIBILITIES)}
          hint="hidden: waits for a start_mission scan code."
          issues={errors.visibility}
          onChange={(v) => set({ visibility: v ?? '' })}
        />
        <CheckboxField
          label="Continues"
          hint='The shelf says "continues in a later update".'
          checked={!!a.continues}
          onChange={(on) => set({ continues: on })}
        />
        <CheckboxField
          label="Dev only"
          hint="Listed only with Dev:TesterTools."
          checked={!!a.devOnly}
          onChange={(on) => set({ devOnly: on })}
        />
      </Fieldset>

      <Fieldset legend="Masthead logo">
        <TextField
          label="Logo text"
          value={logo.text}
          hint="KINGS OF ROUTE 9"
          issues={errors['logo.text']}
          onChange={(v) => set({ logo: { ...logo, text: v } })}
        />
        <div>
          <TextField
            label="Colour"
            mono
            value={logo.color}
            hint="A palette hex (#E3242B)."
            issues={errors['logo.color']}
            onChange={(v) => set({ logo: { ...logo, color: v } })}
          />
          <span
            className="mt-1 inline-block border-2 border-ink px-2 font-bold"
            style={{ color: logoColor(logo.color), background: '#15121a' }}
          >
            {logo.text || a.title || 'LOGO'}
          </span>
        </div>
        <TextField
          label="Sprite"
          mono
          value={logo.sprite}
          hint='UI atlas sprite (logo_kings_route9), or "" to set the text in the masthead font.'
          issues={errors['logo.sprite']}
          onChange={(v) => set({ logo: { ...logo, sprite: v } })}
        />
      </Fieldset>

      <Fieldset legend="Unlock (production)">
        <SelectField
          label="Opens after issue"
          value={unlock.afterIssue}
          none="open from the start"
          options={allIssueOpts}
          hint="The arc opens once this issue is read. Everything is open on the test server."
          issues={errors['unlock.afterIssue']}
          onChange={(v) => set({ unlock: { ...unlock, afterIssue: v ?? '' } })}
        />
        <SelectField
          label="Unlock trigger"
          value={unlock.triggerId}
          none="none"
          options={triggerOpts}
          hint="A hidden arc's start_mission trigger."
          issues={errors['unlock.triggerId']}
          onChange={(v) => set({ unlock: { ...unlock, triggerId: v ?? '' } })}
        />
      </Fieldset>

      <Fieldset legend="Issues (reading order)">
        <ListEditor<string>
          label="Issues"
          hint="Missions with a Story block (edit each in Missions → Story). 3–5 for a finished arc."
          what="issue"
          items={a.issues}
          issues={errors.issues}
          make={() => ''}
          onChange={(v) => set({ issues: v })}
          row={(id, i, put) => (
            <>
              <SelectInput
                label={`Issue ${i + 1}`}
                value={id}
                options={issueOpts}
                invalid={!!errors[`issues.${i}`]?.some((x) => x.severity === 'error')}
                onChange={(v) => put(v ?? '')}
              />
              <IssueList issues={errors[`issues.${i}`]} />
            </>
          )}
        />
        <ListEditor<StoryTeaserDef>
          label="Coming-soon teasers"
          hint='Black covers after the last issue ("#4 The Lab Under the City").'
          what="teaser"
          items={a.teasers}
          issues={errors.teasers}
          make={() => ({ number: a.issues.length + a.teasers.length + 1, title: '' })}
          onChange={(v) => set({ teasers: v })}
          row={(t, i, put) => (
            <div className="grid grid-cols-[6rem_minmax(0,1fr)] gap-2">
              <NumberField
                label="Number"
                integer
                min={1}
                value={t.number}
                issues={errors[`teasers.${i}.number`]}
                onChange={(v) => put({ ...t, number: v ?? 0 })}
              />
              <TextField
                label="Title"
                value={t.title}
                issues={errors[`teasers.${i}.title`]}
                onChange={(v) => put({ ...t, title: v })}
              />
            </div>
          )}
        />
      </Fieldset>

      <Fieldset legend="Cast">
        <ListEditor<CastDef>
          label="Cast"
          hint="The hero and the people issues bind by id. Ids are unique across every arc (cast_dee)."
          what="cast member"
          items={a.cast}
          issues={errors.cast}
          make={() => castDefaults(`cast_${a.cast.length + 1}`)}
          onChange={(v) => set({ cast: v })}
          row={(raw, i, put) => {
            const c = withDefaults(castDefaults(), raw) as Required<CastDef>;
            const at = (f: string) => errors[`cast.${i}.${f}`];
            const p = (patch: Partial<CastDef>) => put({ ...raw, ...patch });
            return (
              <details>
                <summary className="cursor-pointer text-sm">
                  <span className="font-mono">{c.id || '(no id)'}</span> {c.name}
                  {errors[`cast.${i}`] ||
                  Object.keys(errors).some((k) => k.startsWith(`cast.${i}.`)) ? (
                    <span className="ml-2 text-blam">has issues</span>
                  ) : null}
                </summary>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <TextField
                    label="Id"
                    mono
                    value={c.id}
                    issues={at('id')}
                    onChange={(v) => p({ id: v })}
                  />
                  <TextField
                    label="Name"
                    value={c.name}
                    issues={at('name')}
                    onChange={(v) => p({ name: v })}
                  />
                  <SelectField
                    label="Pronouns"
                    value={c.pronouns}
                    options={pronounOpts.length ? pronounOpts : opts(['she', 'he', 'they'])}
                    issues={at('pronouns')}
                    onChange={(v) => p({ pronouns: v ?? 'they' })}
                  />
                  <NumberField
                    label="Look seed"
                    integer
                    min={0}
                    value={c.lookSeed}
                    hint="0 = derived from the id."
                    issues={at('lookSeed')}
                    onChange={(v) => p({ lookSeed: v ?? 0 })}
                  />
                  <CastLookField
                    value={(c as { appearance?: string | null }).appearance}
                    issues={at('appearance')}
                    onChange={(v) => p({ appearance: v } as Parameters<typeof p>[0])}
                  />
                  <NumberField
                    label="Max health"
                    integer
                    min={1}
                    value={c.maxHealth}
                    issues={at('maxHealth')}
                    onChange={(v) => p({ maxHealth: v ?? 0 })}
                  />
                  <SelectField
                    label="NPC"
                    value={c.npcId}
                    none="none"
                    options={npcOpts}
                    hint="When they also appear as an NPC elsewhere."
                    issues={at('npcId')}
                    onChange={(v) => p({ npcId: v ?? '' })}
                  />
                  <StringListEditor
                    label="Wear"
                    value={c.wear}
                    errors={errors}
                    base={`cast.${i}.wear`}
                    placeholder="hospital_gown"
                    hint="Look overrides (story wardrobe: hospital gown, wristband, store vest, biker cut)."
                    onChange={(v) => p({ wear: v })}
                  />
                  <TextField
                    label="Bio"
                    multiline
                    value={c.bio}
                    issues={at('bio')}
                    onChange={(v) => p({ bio: v })}
                  />
                  <LoadoutEditor
                    label="Default loadout"
                    value={c.loadout}
                    errors={errors}
                    base={`cast.${i}.loadout`}
                    onChange={(l) => p({ loadout: l })}
                  />
                </div>
              </details>
            );
          }}
        />
      </Fieldset>

      <Fieldset legend="Endings">
        <ListEditor<EndingDef>
          label="Endings"
          hint="Picked when the last issue is read: first match wins; the last has no condition (the default). Empty for a main arc that continues."
          what="ending"
          items={a.endings}
          issues={errors.endings}
          make={() => endingDefaults(`ending_${a.endings.length + 1}`)}
          onChange={(v) => set({ endings: v })}
          row={(raw, i, put) => {
            const e = withDefaults(endingDefaults(), raw) as Required<EndingDef>;
            const at = (f: string) => errors[`endings.${i}.${f}`];
            const p = (patch: Partial<EndingDef>) => put({ ...raw, ...patch });
            return (
              <div className="grid gap-2 sm:grid-cols-2">
                <TextField
                  label="Id"
                  mono
                  value={e.id}
                  issues={at('id')}
                  onChange={(v) => p({ id: v })}
                />
                <TextField
                  label="Title"
                  value={e.title}
                  issues={at('title')}
                  onChange={(v) => p({ title: v })}
                />
                <SelectField
                  label="Ending beat"
                  value={e.beatId}
                  none="the issue's own outro"
                  options={chainOpts}
                  issues={at('beatId')}
                  onChange={(v) => p({ beatId: v ?? '' })}
                />
                <SelectField
                  label="Ending patch (cosmetic)"
                  value={e.cosmeticId}
                  none="none"
                  options={cosmeticOpts}
                  issues={at('cosmeticId')}
                  onChange={(v) => p({ cosmeticId: v ?? '' })}
                />
                <ConditionPicker
                  label="When"
                  value={e.when}
                  issues={relErrors(errors, `endings.${i}.when`)}
                  onChange={(c) => p({ when: c })}
                />
              </div>
            );
          }}
        />
      </Fieldset>

      <ArcFlagsList
        rows={flags}
        issueTitle={(id) => missions.find((m) => m.id === id)?.title ?? id}
      />
    </>
  );
}

/** The per-arc flags list: each flag with the issues that write it (and the values) and the issues that read it. */
export function ArcFlagsList({
  rows,
  issueTitle,
}: {
  rows: ReturnType<typeof arcFlags>;
  issueTitle: (id: string) => string;
}) {
  const who = (u: { issueId: string; number: number; where: string }) =>
    u.issueId ? `#${u.number} ${issueTitle(u.issueId)} (${u.where})` : u.where;
  return (
    <fieldset className="mb-4 rounded border-2 border-ink bg-white p-3" data-testid="arc-flags">
      <legend className="px-1 text-sm font-bold">Flags (what each issue writes and reads)</legend>
      <p className="mb-2 text-xs text-bruise">
        From flag effects and set_flag actions (writes) and flag.* conditions (reads) in each
        issue&rsquo;s mission, beats and scenarios, plus the endings. The validator rejects a read
        that no earlier issue writes.
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-bruise">No flags yet.</p>
      ) : (
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b-2 border-ink">
              <th className="py-1 pr-2">Flag</th>
              <th className="py-1 pr-2">Values</th>
              <th className="py-1 pr-2">Written by</th>
              <th className="py-1 pr-2">Read by</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-newsprint align-top">
                <td className="py-1 pr-2 font-mono">
                  {r.key}
                  {r.problems.map((p) => (
                    <div key={p} className="font-sans text-blam">
                      {p}
                    </div>
                  ))}
                </td>
                <td className="py-1 pr-2 font-mono">{r.values.join(' | ')}</td>
                <td className="py-1 pr-2">
                  {r.writes.map((u, i) => (
                    <div key={i}>{who(u)}</div>
                  ))}
                </td>
                <td className="py-1 pr-2">
                  {r.reads.map((u, i) => (
                    <div key={i}>{who(u)}</div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </fieldset>
  );
}
